import { createConfigStore } from './store/config-store';
import { getBrandConfig } from './get-brand-config';
import { parseEnv } from './env/env-to-store';
import type { ConfigTypes } from './store/config-store.types';
import type { Brand } from './@types/Brand.types';
import { isBrand } from './@types/Brand.types';
import type { Context } from './@types/Context.types';
import { Contexts } from './@types/Context.types';

type ConfigStore = ReturnType<typeof createConfigStore>;

const isServer = () => typeof window === 'undefined';

/**
 * `ConfigService` handles async server-side bootstrapping and sync client-side initialization of configuration.
 */
export class ConfigService {
  private static _store: ConfigStore | null = null; // Active store for the current request/page.
  private static _brandStores: Map<Brand, ConfigStore> = new Map();

  /**
   * Request-scoped pointer to the active store.
   *
   * `_store` alone is a process-global mutable pointer: on a multi-tenant
   * server, request B's `initialize()` repoints it while request A is suspended
   * on an `await`, so A's later `get()` calls read B's tenant (MV-3107 verified
   * this live: cross-tenant gtag ids). A per-request memoizer installed via
   * `installRequestScope` (React's `cache` in Next apps — wired by
   * `@axo/config/site/request-scope`, keeping this file react-free) gives
   * each request its own ref, so it keeps reading the store its own
   * `bootstrap()` resolved. Uninstalled — or outside a scoped request (client,
   * middleware, Route Handlers, tests) — the ref degrades to a fresh object
   * per call and reads fall back to `_store`, exactly the previous behavior.
   * React's cache dispatcher is only armed during an actual RSC render, so
   * Route Handlers are just as unscoped as middleware: any `get(key)` call
   * there after a real `await` needs the explicit `get(key, brand)` form,
   * same as middleware — this file has no way to enforce that for you.
   */
  private static _requestStoreRef: () => { store: ConfigStore | null } =
    () => ({ store: null });

  /**
   * Installs a per-request memoizer for the active-store ref, isolating
   * concurrent multi-tenant requests (MV-3107). Pass React's `cache` from an
   * RSC app — see `@axo/config/site/request-scope`, which every Next app
   * layout imports. Keeps this file free of a react dependency.
   */
  static installRequestScope(
    scope: <T>(create: () => T) => () => T,
  ): void {
    ConfigService._requestStoreRef = scope(() => ({
      store: null as ConfigStore | null,
    }));
  }

  /**
   * Boots up the configuration on the server.
   * Resolves the tenant dynamically, then initializes the store.
   */
  static async bootstrap(
    contexts: Context[] = [...Contexts],
  ): Promise<typeof ConfigService> {
    let brand: Brand | undefined;

    if (isServer()) {
      try {
        const { getRequestTenant } = await import(
          './site/multi-tenant/get-request-tenant'
        );
        brand = await getRequestTenant();
      } catch (error) {
        console.warn('Failed to resolve tenant dynamically:', error);
      }

      if (brand && !ConfigService._brandStores.has(brand)) {
        console.info(
          ` ✓ ConfigService :: bootstrap : ${brand} – ${process.env.NEXT_PUBLIC_ENV} – 📦 v${process.env.pkg_version}`,
        );
      }
    }

    // Initialize with the resolved tenant. When resolution failed (warm or
    // cold), don't pin this request to the ambient `_store` — a concurrent
    // request's `initialize()` may have already repointed it during the
    // `await getRequestTenant()` above, which is the exact cross-tenant race
    // this file exists to close. `initialize()` falls back to its own
    // env-derived default: deterministically wrong is safer than racily
    // wrong.
    ConfigService.initialize(brand, contexts);

    return ConfigService;
  }

  /**
   * Initializes the store for a given brand.
   *
   * Stores are immutable after creation and cached per brand.
   * Both server (bootstrap/middleware) and client (InitializeClientSiteConfig)
   * share the same brand-keyed cache — no server/client distinction needed.
   *
   * @param brand - Optional tenant/brand
   * @param contexts - Configuration contexts
   */
  static initialize(
    brand?: Brand,
    contexts: Context[] = [...Contexts],
  ): typeof ConfigService {
    // Fallback to environment-specified brand
    brand ??= (
      typeof process !== 'undefined'
        ? process.env.SITE || process.env.NEXT_PUBLIC_SITE
        : undefined
    ) as Brand;

    if (!isBrand(brand)) {
      console.warn(
        `Invalid brand "${brand}" detected. Defaulting to "axofinans.se".`,
      );
      brand = 'axofinans.se';
    }

    if (!ConfigService._brandStores.has(brand)) {
      // Retrieve brand-specific configuration
      const config = getBrandConfig(brand, contexts);

      // Add parsed environment variables to the configuration
      config.env = parseEnv();

      const store = createConfigStore({ config });
      ConfigService._brandStores.set(brand, store);

      const env =
        typeof process !== 'undefined'
          ? process.env.NEXT_PUBLIC_ENV
          : undefined;
      const pkgVersion =
        typeof process !== 'undefined' ? process.env.pkg_version : undefined;
      console.info(
        ` ✓ ConfigService :: store initialized : ${brand} – ${env} – 📦 v${pkgVersion}`,
      );
    }

    ConfigService._store = ConfigService._brandStores.get(brand)!;
    // Pin this request's store; a no-op outside RSC requests (fresh ref).
    ConfigService._requestStoreRef().store = ConfigService._store;

    return ConfigService;
  }

  /**
   * Retrieves the active store (must call `initialize` or `bootstrap()` first).
   */
  private static getStore(): ConfigStore {
    const requestStore = ConfigService._requestStoreRef().store;
    if (requestStore) return requestStore;

    if (!ConfigService._store) {
      throw new Error(
        'ConfigService is uninitialized. Call `ConfigService.bootstrap()` or `initialize()` first.',
      );
    }
    return ConfigService._store;
  }

  /**
   * Exposes the `register` API for adding new values to the store.
   */
  static register<K extends keyof ConfigTypes>(
    key: K,
    value: ConfigTypes[K],
  ): void {
    ConfigService.getStore().getState().register(key, value);
  }

  /**
   * Retrieves a value from the store.
   *
   * @param key - the config context key
   * @param brand - when supplied, reads the immutable per-brand store
   *   directly instead of the ambient one. Use on non-RSC server paths that
   *   read config after an `await` (e.g. middleware, where the request-scoped
   *   ref does not apply) so a concurrent request can't flip the ambient
   *   store to another tenant mid-flight (MV-3107).
   */
  static get<K extends keyof ConfigTypes>(
    key: K,
    brand?: Brand,
  ): ConfigTypes[K] {
    if (brand !== undefined) {
      // Never fall back to the ambient store here — that would silently read
      // another tenant's config, the exact leak the explicit param prevents.
      // Strict undefined check: an empty-string brand must throw below, not
      // degrade to the ambient path.
      const store = ConfigService._brandStores.get(brand);
      if (!store) {
        throw new Error(
          `ConfigService is uninitialized for brand "${brand}". Call \`initialize("${brand}")\` first.`,
        );
      }
      return store.getState().get(key);
    }

    return ConfigService.getStore().getState().get(key);
  }

  /**
   * Retrieves all configuration values from the store.
   */
  static getAll(): Partial<ConfigTypes> {
    return ConfigService.getStore().getState().getAll();
  }
}
